import MintTokenWallet from "@/components/MintTokenComponents/MintTokenWallet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { buyTokens, sellTokens } from "@/components/web3/TokenPublic";
import { useToast } from "@/hooks/use-toast";
import { usePrivy, useSolanaWallets } from "@privy-io/react-auth";
import React, { useEffect, useRef, useState } from "react";

// IMPORTANT: For Lightweight Charts v5 usage
import { AreaSeries, createChart } from "lightweight-charts";

import CandleStickChart from "@/components/Chart";
import { Loader2 } from "lucide-react";
import { useGraduatingTokenQuery, useTokenPriceQuery } from "@/api/tokens";
import { formatLargeNumber } from "@/lib/utils";

interface TokenData {
  name: string;
  symbol: string;
  persona_id?: string;
}

interface TokenRowProps {
  tokenStage?: "minted" | "graduated";
  blockchain?: string;
  contractAddress?: string;
  bondingCurveData?: {
    poolAddress: string;
    poolTokenAccount: string;
    poolSolVault: string;
  };
  raydiumPoolAddress?: string;
  token?: TokenData;
}

interface BondingCurveGraphData {
  timestamp: number;
  price: number;
}

interface PriceMetrics {
  liquidity: string;
  marketCap: string;
  price?: number;
  dailyVolume?: string;
  priceChangePercent?: number;
  totalSupply?: string;
}

const graduationThreshold = import.meta.env.VITE_GRADUATION_THRESHOLD;

// Utility to format large numbers (K, M, etc.)
const formatNumber = (num: number | undefined): string => {
  if (!num) return "0";
  if (num >= 0.9e9) return `${(num / 1e9).toFixed(2)}B`; // Lower threshold to 995M
  if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
  if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
  return num.toFixed(0);
};

const TokenRow: React.FC<TokenRowProps> = ({
  tokenStage,
  blockchain,
  contractAddress,
  bondingCurveData,
  raydiumPoolAddress,
  token,
}) => {
  const { login, authenticated } = usePrivy();
  const { wallets } = useSolanaWallets();
  const { toast } = useToast();
  const [amount, setAmount] = useState("");
  const [tradeLoading, setTradeLoading] = useState(false);
  const personaId = token?.persona_id;
  const hasToken = !!personaId && !!contractAddress;

  const bondingCurvePoolAddress = bondingCurveData?.poolAddress;
  const { data: graduatingTokenData, isPending: isGraduatingTokenPending } =
    useGraduatingTokenQuery(personaId!, contractAddress!, {
      enabled: hasToken && tokenStage === "minted",
    });

  const {
    data: graduatedTokenPriceData,
    isPending: isGraduatedTokenPricePending,
  } = useTokenPriceQuery(
    contractAddress || "",
    raydiumPoolAddress || "",
    blockchain || "",
    { enabled: hasToken && tokenStage === "graduated" },
  );

  const isPending =
    (tokenStage === "minted" && isGraduatingTokenPending) ||
    (tokenStage === "graduated" && isGraduatedTokenPricePending);

  let tokenPriceData: any;

  let priceMetrics: PriceMetrics | undefined;
  let totalSwaps24h = 0;
  let chartData: { time: number; value: number }[] = [];

  if (!isPending && tokenStage === "minted") {
    tokenPriceData = graduatingTokenData;
    priceMetrics = {
      // @ts-expect-error incomplete types
      lastTransaction: graduatingTokenData?.pool_state.lastTransaction,
      // @ts-expect-error incomplete types
      liquidity: graduatingTokenData?.pool_state.liquidity,
      // @ts-expect-error incomplete types
      marketCap: formatLargeNumber(graduatingTokenData?.pool_state.marketCap),
      // @ts-expect-error incomplete types
      price: graduatingTokenData?.pool_state.price,
    };

    const bondingCurveGraphData: BondingCurveGraphData[] =
      // @ts-expect-error incomplete types
      graduatingTokenData?.pool_minutely_data;

    chartData = bondingCurveGraphData
      ? bondingCurveGraphData
          .map((data) => {
            return {
              time: new Date(data.timestamp).getTime() / 1000,
              value: data.price,
            };
          })
          .sort((a, b) => a.time - b.time)
      : [];
  } else if (!isPending && tokenStage === "graduated") {
    tokenPriceData = graduatedTokenPriceData;
    priceMetrics = {
      liquidity: graduatedTokenPriceData?.marketMetrics
        ? formatLargeNumber(graduatedTokenPriceData?.marketMetrics.liquidity, {
            decimals: 0,
          })
        : "-",
      marketCap: graduatedTokenPriceData?.marketMetrics
        ? formatLargeNumber(graduatedTokenPriceData?.marketMetrics.marketCap, {
            decimals: 0,
          })
        : "-",
      price: graduatedTokenPriceData?.price.price,
      dailyVolume: graduatedTokenPriceData?.price
        ? formatLargeNumber(graduatedTokenPriceData?.price.volume24h, {
            decimals: 0,
          })
        : "-",
      priceChangePercent:
        Number(graduatedTokenPriceData?.price.variation24h) || 0,
      // @ts-expect-error missing type for field
      high24h: Number(graduatedTokenPriceData?.price.price24h) || 0,
      // @ts-expect-error missing type for field
      low24h: Number(graduatedTokenPriceData?.price.priceChain24h) || 0,
      totalSupply: graduatedTokenPriceData?.marketMetrics
        ? formatNumber(graduatedTokenPriceData?.marketMetrics.totalSupply)
        : "-",
    };
    // If we do have data, gather 24h swap stats
    totalSwaps24h =
      (tokenPriceData?.price?.buys24h || 0) +
      (tokenPriceData?.price?.sells24h || 0);
  }

  const handleTrade = async (action: "buy" | "sell") => {
    if (!amount || isNaN(parseFloat(amount))) {
      toast({
        variant: "destructive",
        description: "Please enter a valid amount",
      });
      return;
    }

    setTradeLoading(true);
    try {
      const solWallet = wallets?.[0];
      if (!solWallet) throw new Error("No wallet connected");

      const parsedAmount = parseFloat(amount);

      if (action === "buy") {
        const result = await buyTokens(
          solWallet,
          parsedAmount,
          tokenStage!,
          contractAddress!,
          bondingCurveData,
          raydiumPoolAddress!,
        );
        if (result) {
          toast({ description: "Buy order successful!" });
        } else {
          toast({ description: "Buy order failed!" });
        }
      } else {
        const result = await sellTokens(
          solWallet,
          parsedAmount,
          tokenStage!,
          contractAddress!,
          bondingCurveData,
          raydiumPoolAddress!,
        );
        if (result) {
          toast({ description: "Sell order successful!" });
        } else {
          toast({ description: "Sell order failed!" });
        }
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        description: `Failed to ${action} tokens: ${error.message}`,
      });
    } finally {
      setTradeLoading(false);
      setAmount("");
    }
  };

  if (hasToken && isPending) {
    return (
      <div className="flex items-center justify-center w-full h-[500px] bg-[#121212] rounded-lg">
        <Loader2 className="w-8 h-8 text-[#E2FF67] animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative z-0">
      {/* 
        If there's no token or no pool, show an overlay. 
        This also sets the below content to partial opacity.
      */}
      {!bondingCurvePoolAddress && !raydiumPoolAddress && (
        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-25 z-[10] rounded-lg">
          <div className="bg-background border rounded-lg shadow-xl p-4 text-center">
            <p className="text-sm">
              {!token ? "Token Coming Soon" : "Token Coming Soon"}
            </p>
          </div>
        </div>
      )}

      <div
        className={`flex flex-col md:flex-row gap-6 ${
          !token || (!bondingCurvePoolAddress && !raydiumPoolAddress)
            ? "opacity-50"
            : ""
        }`}
      >
        <div className="w-full md:w-[65%] bg-[#0A0A0A] rounded-lg p-4">
          {tokenStage === "minted" && (
            <div className="mb-4">
              <div className="text-[#E2FF67] text-xl font-semibold">
                Bonding curve in progress
              </div>
              <div className="text-gray-400 text-sm mt-1">
                <p>
                  Graduate this coin to Raydium at {graduationThreshold} market
                  cap.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-white text-xl font-semibold flex items-center gap-2">
                {token?.symbol || "TOKEN"}/SOL
              </h2>
              {/* Bigger, yellow price */}
              <div className="text-[#E2FF67] text-3xl font-bold mt-1">
                ${priceMetrics?.price ? priceMetrics.price.toFixed(6) : ""}
                {priceMetrics?.priceChangePercent && (
                  <span
                    className={`ml-2 text-sm ${
                      priceMetrics.priceChangePercent >= 0
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  >
                    {priceMetrics.priceChangePercent >= 0 ? "+" : ""}
                    {priceMetrics.priceChangePercent.toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
          </div>

          {tokenStage === "minted" ? (
            <ChartComponent data={chartData} />
          ) : (
            <CandleStickChart
              pool={raydiumPoolAddress}
              blockchain={blockchain}
            />
          )}
        </div>

        <div className="w-full md:w-[35%] space-y-4">
          <div className="flex flex-col pt-4 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-[#E2FF67] text-xl">
                Market cap: {priceMetrics?.marketCap ?? ""}
              </span>
            </div>
          </div>

          {tokenStage === "minted" && tokenPriceData ? (
            <Card className="bg-[#121212] border-none shadow-lg p-2">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-gray-400 text-sm">Market Cap</div>
                  <div className="text-white text-lg">
                    {priceMetrics!.marketCap}
                  </div>
                </div>

                <div>
                  <div className="text-gray-400 text-sm">Liquidity</div>
                  <div className="text-white text-lg">
                    {priceMetrics!.liquidity}
                  </div>
                </div>
              </div>
            </Card>
          ) : null}

          {tokenStage === "graduated" && tokenPriceData ? (
            <Card className="bg-[#121212] border-none shadow-lg p-2">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-gray-400 text-sm">FDV</div>
                  <div className="text-white text-lg">
                    {priceMetrics!.marketCap}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Volume (24h)</div>
                  <div className="text-white text-lg">
                    {priceMetrics!.dailyVolume}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Liquidity (24h)</div>
                  <div className="text-white text-lg">
                    {priceMetrics!.liquidity}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Swaps (24h)</div>
                  <div className="text-white text-lg">{totalSwaps24h}</div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Holders</div>
                  <div className="text-white text-lg">
                    {formatNumber(
                      parseInt(tokenPriceData?.marketMetrics?.holders || 0),
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-gray-400 text-sm">Total Supply</div>
                  <div className="text-white text-lg">
                    {formatNumber(
                      tokenPriceData?.marketMetrics?.totalSupply || 0,
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ) : null}

          <Card className="bg-[#121212] border-none shadow-lg">
            <CardContent className="pt-6 pl-0">
              <Tabs defaultValue="buy">
                <TabsList className="grid w-full grid-cols-2 mb-4 bg-[#1A1A1A]">
                  <TabsTrigger
                    value="buy"
                    className="data-[state=active]:bg-[#E2FF67] data-[state=active]:text-black"
                  >
                    Buy
                  </TabsTrigger>
                  <TabsTrigger
                    value="sell"
                    className="data-[state=active]:bg-[#E2FF67] data-[state=active]:text-black"
                  >
                    Sell
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="buy">
                  <div className="space-y-3">
                    <div className="relative">
                      <Input
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="bg-[#1A1A1A] border-none text-white h-14 text-lg pl-4 pr-16"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                        SOL
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      {["0.1", "0.5", "1"].map((value) => (
                        <Button
                          key={value}
                          variant="outline"
                          onClick={() => setAmount(value)}
                          className="bg-black text-white border-none hover:bg-[#1A1A1A]"
                        >
                          {value} SOL
                        </Button>
                      ))}
                    </div>

                    {tokenStage &&
                      (!wallets?.length ? (
                        <MintTokenWallet
                          authenticated={authenticated}
                          wallets={wallets}
                          login={login}
                        />
                      ) : (
                        <Button
                          className="w-full bg-[#E2FF67] text-black hover:bg-[#E2FF67]/90 h-10 text-lg font-medium"
                          onClick={() => handleTrade("buy")}
                          disabled={tradeLoading}
                        >
                          {tradeLoading ? "Processing..." : "Swap"}
                        </Button>
                      ))}
                  </div>
                </TabsContent>

                <TabsContent value="sell">
                  <div className="space-y-3">
                    <div className="relative">
                      <Input
                        placeholder="0.00"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="bg-[#1A1A1A] border-none text-white h-14 text-lg pl-4 pr-16"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                        {token?.symbol || "TOKEN"}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      {["100", "500", "1000"].map((value) => (
                        <Button
                          key={value}
                          variant="outline"
                          onClick={() => setAmount(value)}
                          className="bg-black text-white border-none hover:bg-[#1A1A1A]"
                        >
                          {value}
                        </Button>
                      ))}
                    </div>

                    {tokenStage &&
                      (!wallets?.length ? (
                        <MintTokenWallet
                          authenticated={authenticated}
                          wallets={wallets}
                          login={login}
                        />
                      ) : (
                        <Button
                          className="w-full bg-[#E2FF67] text-black hover:bg-[#E2FF67]/90 h-10 text-lg font-medium"
                          onClick={() => handleTrade("sell")}
                          disabled={tradeLoading}
                        >
                          {tradeLoading ? "Processing..." : "Swap"}
                        </Button>
                      ))}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const ChartComponent = ({
  data,
}: {
  data: { time: number; value: number }[];
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      chart.applyOptions({ width: chartContainerRef.current!.clientWidth });
    };

    const chart = createChart(chartContainerRef.current!, {
      layout: {
        background: { color: "#0A0A0A" },
        textColor: "#d1d4dc",
      },
      grid: {
        vertLines: { color: "#1c1c1c" },
        horzLines: { color: "#1c1c1c" },
      },
      rightPriceScale: {
        borderVisible: false,
      },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
      },
      width: chartContainerRef.current!.clientWidth,
      height: 325,
    });
    chart.timeScale().fitContent();

    const newSeries = chart.addSeries(AreaSeries, {
      lineColor: "#E2FF67",
      topColor: "#E2FF67",
      bottomColor: "rgba(226, 255, 103, 0.04)",
      lineWidth: 2,
    });
    // @ts-expect-error wrong type
    newSeries.setData(data);

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);

      chart.remove();
    };
  }, [data]);

  return <div ref={chartContainerRef} />;
};

export default TokenRow;
